import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PlusIcon, TrashIcon, PencilSimpleIcon, BuildingsIcon } from "@phosphor-icons/react";

const SECTIONS = ["Indoor", "Outdoor", "Bar", "Private", "Rooftop"];
const EMPTY_TABLE = { number: "", capacity: 4, section: "Indoor", x: 10, y: 10, is_active: true };

const SECTION_TONE = {
  Indoor: "bg-primary/10 text-primary border-primary/30",
  Outdoor: "bg-[hsl(140_40%_92%)] text-[hsl(140_40%_30%)] border-[hsl(140_40%_60%)]",
  Bar: "bg-[hsl(45_82%_88%)] text-[hsl(38_62%_35%)] border-[hsl(45_82%_60%)]",
  Private: "bg-secondary/15 text-secondary border-secondary/40",
  Rooftop: "bg-muted text-muted-foreground border-border",
};

export default function TablesPanel() {
  const { activeId, active } = useProperty();
  const [tables, setTables] = useState([]);
  const [tab, setTab] = useState("Indoor");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TABLE);
  const [bulk, setBulk] = useState({ count: 6, start_number: 1, prefix: "T", capacity: 4, section: "Indoor" });
  const canvasRef = useRef(null);

  const load = () => activeId && api.get("/tables", { params: { property_id: activeId } }).then((r) => setTables(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const filtered = useMemo(() => tables.filter((t) => t.section === tab), [tables, tab]);
  const sectionCounts = useMemo(() => {
    const c = {};
    SECTIONS.forEach((s) => { c[s] = tables.filter((t) => t.section === s).length; });
    return c;
  }, [tables]);

  const save = async () => {
    if (!form.number) return toast.error("Table number required");
    try {
      if (editing) {
        await api.patch(`/tables/${editing.id}`, form);
        toast.success("Table updated");
      } else {
        await api.post("/tables", { ...form, property_id: activeId });
        toast.success("Table added");
      }
      setDialogOpen(false); setEditing(null); setForm(EMPTY_TABLE);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ${t.number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/tables/${t.id}`);
      toast.success("Table deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const bulkCreate = async () => {
    try {
      const r = await api.post("/tables/bulk", { property_id: activeId, ...bulk });
      toast.success(`${r.data.created.length} tables created: ${r.data.created.join(", ")}`);
      setBulkOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // ----- Drag-and-drop on floor plan -----
  const onDragEnd = async (id, x, y) => {
    try {
      await api.patch(`/tables/${id}`, { x, y });
      setTables((ts) => ts.map((t) => t.id === id ? { ...t, x, y } : t));
    } catch (e) { toast.error("Save position failed"); }
  };

  return (
    <div>
      <Card className="p-3 mb-4 flex gap-3 items-center bg-primary/5 border-primary/30">
        <BuildingsIcon size={16} weight="duotone" className="text-primary" />
        <p className="text-xs text-muted-foreground">
          Tables are <strong>property-specific</strong> — you're editing the floor plan for
          <strong className="text-foreground"> {active?.name || "the selected hotel"}</strong>. Switch properties from the sidebar to edit another hotel's tables.
        </p>
      </Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="label-overline">Restaurant floor plan</p>
          <h3 className="font-display text-2xl">Tables · {active?.name || "select a property"}</h3>
          <p className="text-xs text-muted-foreground mt-1">Configure dine-in tables by section. Drag any table on the floor plan to reposition it — position is saved automatically.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="tables-bulk-btn">Bulk add</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Bulk-add tables</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Section</Label>
                  <Select value={bulk.section} onValueChange={(v) => setBulk({ ...bulk, section: v })}>
                    <SelectTrigger data-testid="tables-bulk-section"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Prefix</Label><Input value={bulk.prefix} onChange={(e) => setBulk({ ...bulk, prefix: e.target.value })} data-testid="tables-bulk-prefix" /></div>
                <div><Label>Start #</Label><Input type="number" value={bulk.start_number} onChange={(e) => setBulk({ ...bulk, start_number: Number(e.target.value) })} data-testid="tables-bulk-start" /></div>
                <div><Label>Count</Label><Input type="number" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: Number(e.target.value) })} data-testid="tables-bulk-count" /></div>
                <div className="col-span-2"><Label>Default capacity</Label><Input type="number" value={bulk.capacity} onChange={(e) => setBulk({ ...bulk, capacity: Number(e.target.value) })} /></div>
              </div>
              <DialogFooter><Button onClick={bulkCreate} data-testid="tables-bulk-submit">Create {bulk.count} tables</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) { setEditing(null); setForm(EMPTY_TABLE); } }}>
            <DialogTrigger asChild><Button size="sm" data-testid="tables-add-btn"><PlusIcon size={14} className="mr-1" /> Add table</Button></DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit table" : "New table"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Number *</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="T1, VIP-3…" data-testid="table-form-number" /></div>
                <div><Label>Section</Label>
                  <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                    <SelectTrigger data-testid="table-form-section"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} data-testid="table-form-capacity" /></div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="table-form-save">{editing ? "Save changes" : "Add table"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {SECTIONS.map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors ${tab === s ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
            data-testid={`tables-section-tab-${s}`}
          >
            {s} <Badge variant="outline" className="ml-1 text-[10px] px-1">{sectionCounts[s]}</Badge>
          </button>
        ))}
      </div>

      {/* Floor plan canvas */}
      <Card className="p-4 mb-4">
        <p className="label-overline mb-2">{tab} — floor layout</p>
        <div
          ref={canvasRef}
          className="relative w-full rounded-lg border-2 border-dashed border-border bg-muted/20"
          style={{ minHeight: 380 }}
          data-testid={`tables-canvas-${tab}`}
        >
          {filtered.length === 0 && (
            <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground italic">
              No tables in {tab} yet. Click "Add table" or "Bulk add" above.
            </p>
          )}
          {filtered.map((t) => (
            <DraggableTable
              key={t.id}
              table={t}
              onDragEnd={onDragEnd}
              onEdit={() => { setEditing(t); setForm(t); setDialogOpen(true); }}
              onDelete={() => remove(t)}
            />
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Drag any table tile to reposition. Positions are stored as percentages, so the layout adapts to any screen size.</p>
      </Card>
    </div>
  );
}

function DraggableTable({ table, onDragEnd, onEdit, onDelete }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);

  const start = (e) => {
    e.preventDefault();
    setDragging(true);
    const canvas = ref.current.parentElement;
    const rect = canvas.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = table.x;
    const origY = table.y;
    const onMove = (ev) => {
      const dx = ((ev.clientX - startX) / rect.width) * 100;
      const dy = ((ev.clientY - startY) / rect.height) * 100;
      const nx = Math.max(0, Math.min(90, origX + dx));
      const ny = Math.max(0, Math.min(88, origY + dy));
      ref.current.style.left = nx + "%";
      ref.current.style.top = ny + "%";
      ref.current.dataset.x = nx.toFixed(2);
      ref.current.dataset.y = ny.toFixed(2);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDragging(false);
      const x = Number(ref.current.dataset.x ?? table.x);
      const y = Number(ref.current.dataset.y ?? table.y);
      onDragEnd(table.id, x, y);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div
      ref={ref}
      onMouseDown={start}
      className={`absolute select-none cursor-move rounded-lg border-2 p-2 min-w-[76px] shadow-sm bg-card ${dragging ? "ring-2 ring-primary shadow-lg" : ""} ${SECTION_TONE[table.section] || ""}`}
      style={{ left: `${table.x}%`, top: `${table.y}%` }}
      data-testid={`table-tile-${table.number}`}
    >
      <div className="text-center">
        <p className="font-display text-lg leading-none">{table.number}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5">{table.capacity} seats</p>
      </div>
      <div className="flex justify-center gap-0.5 mt-1">
        <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-0.5 hover:bg-primary/20 rounded" data-testid={`table-tile-edit-${table.number}`}>
          <PencilSimpleIcon size={10} />
        </button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-0.5 hover:bg-destructive/20 rounded text-destructive" data-testid={`table-tile-delete-${table.number}`}>
          <TrashIcon size={10} />
        </button>
      </div>
    </div>
  );
}
